/**
 * Regex for valid URLs.
 */
const urlRegex =
  /\b(?:https?:\/\/|www\.)(?:(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?\.)+\p{L}{2,}|(?:\d{1,3}\.){3}\d{1,3}|\[[A-Fa-f0-9:.]+\])(?::\d{2,5})?(?:\/[^\s<>()[\]{}"]*)?(?:\?[^ \t\r\n<>()[\]{}"]*)?(?:#[^ \t\r\n<>()[\]{}"]*)?/gu

/**
 * An extracted URL.
 */
export interface ExtractedUrl {
  /**
   * The extracted URL.
   */
  url: string

  /**
   * Start index of the URL in the source text.
   */
  startIndex: number

  /**
   * End index of the URL in the source text.
   */
  endIndex: number
}

/**
 * Extracts URLs from the given text.
 *
 * @param text
 */
export function extractUrls(text: string): ExtractedUrl[] {
  const results: ExtractedUrl[] = []
  const matches = text.matchAll(urlRegex)

  for (const match of matches) {
    results.push({
      url: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    })
  }

  return results
}

/**
 * Token returned from {@link splitByUrls}.
 */
export type UrlSplatToken =
  | { type: 'Text'; text: string }
  | { type: 'Url'; url: string }

/**
 * Splits the text into tokens from extracted URLs.
 *
 * @param text
 */
export function splitByUrls(text: string): UrlSplatToken[] {
  const tokens: UrlSplatToken[] = []
  if (!text) {
    return tokens
  }

  const matches = extractUrls(text)
  if (matches.length === 0) {
    tokens.push({
      type: 'Text',
      text,
    })
    return tokens
  }

  let lastUrlEndIndex = 0

  for (const match of matches) {
    const textBeforeMatch = text.slice(lastUrlEndIndex, match.startIndex)
    if (textBeforeMatch) {
      tokens.push({
        type: 'Text',
        text: textBeforeMatch,
      })
    }

    tokens.push({
      type: 'Url',
      url: match.url,
    })

    lastUrlEndIndex = match.endIndex
  }

  const remainingText = text.slice(lastUrlEndIndex)
  if (remainingText) {
    tokens.push({
      type: 'Text',
      text: remainingText,
    })
  }

  return tokens
}
